/*
   This component, EventsCalendarView, displays a calendar view of upcoming events.
   It includes a date selector, event cards, and handles data fetching for events based on the selected date via server actions.
   The component integrates with Swiper for horizontal scrolling of events and includes loading skeletons to enhance user experience during data fetches.
*/

'use client';

import { EventCard } from '@/sanity/fetchEvents';
import Skeleton from '../base/Skeleton';
import { useCallback, useEffect, useState, useRef } from 'react';
import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/mousewheel';
import '@/styles/vendor/swiper.css';
import { Scrollbar, Mousewheel } from 'swiper/modules';
import { EventsCalendarDaySelector } from './EventsCalendarDaySelector';
import { SoonEmptyData, SoonEventsResult } from '@/shared/types/contentModule';
import EventsCalendarEmpty from './EventsCalendarEmpty';
import { updateSlidesOffset } from '@/utils/updateSlidesOffset';
import { renderInlineMarkup } from '@/utils/renderInlineMarkup';
import { useServerTime } from '@/hooks/useServerTime';
import {
  MATOMO_ACTIONS,
  MATOMO_CATEGORIES,
  MATOMO_NAMES,
  trackMatomoEvent,
} from '@/utils/matomo';
import { formatISO } from 'date-fns';
import layoutStyles from '@/styles/section-layouts.module.scss';
import styles from './EventsCalendarView.module.scss';
import EventsCalendarViewEventSlide from './EventsCalendarViewEventSlide';

type EventsCalendarViewProps = {
  className?: string;
  initialEmptyTeaser: SoonEmptyData | null;
  initialEvents: EventCard[] | undefined;
  initialDate: Date;
  // Server-prerendered time as an immediate fallback. The authoritative,
  // tamper-proof time is fetched at runtime from /api/server-time.
  initialServerTime: string;
  title: string;
  caption: string;
  fetchEventsFromServer: (date: string) => Promise<SoonEventsResult>;
};

export default function EventsCalendarView({
  className,
  initialEmptyTeaser,
  initialEvents,
  initialDate,
  initialServerTime,
  title,
  caption,
  fetchEventsFromServer,
}: EventsCalendarViewProps) {
  const [events, setEvents] = useState(initialEvents);
  // The empty state comes per date from the API (TEASER_EMPTY_MODULE) and is
  // updated together with the events on every date change.
  const [emptyTeaser, setEmptyTeaser] = useState(initialEmptyTeaser);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  // Starts with the prerendered server time and is replaced at runtime by the
  // fresh time from /api/server-time. Deliberately without the client-clock
  // fallback: the date boundaries must not be shiftable by changing the system
  // clock.
  const serverNow = useServerTime(initialServerTime);
  const [slidesOffsetBefore, setSlidesOffsetBefore] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const swiperRef = useRef<SwiperRef>(null);
  const [showSkeleton, setShowSkeleton] = useState(true);

  const [MouseWheelEnabled, setMouseWheelEnabled] = useState(true);

  // Fetch events for the selected date
  const fetchEvents = useCallback(
    async (date: Date) => {
      setLoading(true);
      try {
        const formattedDate = formatISO(date, { representation: 'date' });
        const result = await fetchEventsFromServer(formattedDate);
        setEvents(result.events);
        setEmptyTeaser(result.emptyTeaser);
      } catch (error) {
        console.error('EventsCalendarView: fetchEvents failed', error);
      } finally {
        setLoading(false);
      }
    },
    [fetchEventsFromServer],
  );

  useEffect(() => {
    if (selectedDate !== initialDate) {
      void fetchEvents(selectedDate);
    }
  }, [selectedDate, fetchEvents, initialDate]);

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);

    // Reset swiper position after changing the date
    if (swiperRef.current) {
      swiperRef.current?.swiper?.slideTo(0);
    }
  };

  useEffect(() => {
    // to view the swiper in the correct layout we need to calculate the offset
    updateSlidesOffset(setSlidesOffsetBefore, true);
    const handleResize = () => updateSlidesOffset(setSlidesOffsetBefore);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Show loading skeleton initially
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  const ticketLinkOnClick = () => {
    trackMatomoEvent(
      MATOMO_CATEGORIES.INTERACTIONS,
      MATOMO_ACTIONS.TICKET_CLICK,
      MATOMO_NAMES.CALENDAR_TICKET_CLICK,
    );
  };

  const locationOnClick = () => {
    trackMatomoEvent(
      MATOMO_CATEGORIES.INTERACTIONS,
      MATOMO_ACTIONS.LOCATION_CLICK,
      MATOMO_NAMES.CALENDAR_LOCATION_CLICK,
    );
  };

  return (
    <div className={className}>
      <div className={[styles.header, layoutStyles.containerContent].join(' ')}>
        <h1 className={styles.headerTitle}>
          {renderInlineMarkup(title, { underlineClassName: styles.underlineFullWidth })}
        </h1>
        <div className={styles.headerCopy}>{caption}</div>
      </div>
      {/* Date selector component */}
      <EventsCalendarDaySelector
        className={styles.daySelectorSpacing}
        onDateClick={handleDateClick}
        serverNow={serverNow}
      />

      {/* Loading skeleton while events are being fetched */}
      {showSkeleton && <LoadingSkeleton />}

      {/* Empty state if the day has no entries */}
      {!showSkeleton && events?.length === 0 ? (
        <EventsCalendarEmpty emptyTeaser={emptyTeaser} />
      ) : (
        <div className={styles.swiperContainer}>
          <Swiper
            slidesPerView="auto"
            ref={swiperRef}
            spaceBetween={16}
            scrollbar={{
              hide: showSkeleton ? true : false,
              el: '.custom-swiper-scrollbar',
              draggable: true,
            }}
            modules={[Scrollbar, Mousewheel]}
            mousewheel={{
              forceToAxis: true,
            }}
            onLock={() => setMouseWheelEnabled(false)}
            onUnlock={() => setMouseWheelEnabled(true)}
            className={[
              'mySwiper',
              !MouseWheelEnabled ? 'swiper-no-mousewheel' : undefined,
            ]
              .filter(Boolean)
              .join(' ')}
            observer={true}
            observeParents={true}
            slidesOffsetBefore={slidesOffsetBefore}
            slidesOffsetAfter={32}
          >
            {!showSkeleton &&
              events?.map((event: EventCard, index: number) => (
                <SwiperSlide
                  key={event._id}
                  className={[styles.slide, loading ? styles.slideLoading : undefined]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <EventsCalendarViewEventSlide
                    event={event}
                    position={index + 1}
                    selectedDate={selectedDate}
                    serverNow={serverNow}
                    locationOnClick={locationOnClick}
                    ticketLinkOnClick={ticketLinkOnClick}
                  />
                </SwiperSlide>
              ))}
          </Swiper>
        </div>
      )}
      {!showSkeleton && events && events.length > 0 && (
        <div
          className={[styles.scrollbarWrapper, layoutStyles.containerContent].join(' ')}
        >
          <div className={['custom-swiper-scrollbar', styles.scrollbar].join(' ')}></div>
        </div>
      )}
    </div>
  );
}

// Skeleton card component for loading state
function SkeletonCard() {
  return (
    <div className={styles.skeletonCard}>
      <Skeleton className={styles.skeletonImage} />
      <div className={styles.skeletonContent}></div>
    </div>
  );
}

// Loading skeleton component
function LoadingSkeleton() {
  return (
    <div className={[styles.skeletonWrapper, layoutStyles.containerContent].join(' ')}>
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
}
